import MDBox from "components/MDBox";
import {
  ProductMeasurementJob,
  ProposalProduct,
  ProposalStage,
} from "generated/graphql";
import useUpdateProposalProduct from "hooks/proposals/useUpdateProposalProduct";
import useAddProposalEquipmentPieces from "hooks/proposals/useAddProposalEquipmentPieces";
import useAddProposalCrews from "hooks/proposals/useAddProposalCrews";
import useAddProposalAdditionalCosts from "hooks/proposals/useAddProposalAdditionalCosts";
import useDeleteProposalMaterialNoPlant from "hooks/proposals/useDeleteProposalMaterialNoPlant";
import useDeleteProposalPlantProduct from "hooks/proposals/useDeleteProposalPlantProduct";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import useModalState from "hooks/useModalState";
import getLocalString from "constants/Localization";
import measurementMapTranslations from "constants/measurementMapTranslation";
import getGraphQLErrorMessage from "utils/graphQL/getGraphQLErrorMessage";
import {
  getDefaultValues,
  UpdateProposalProductInputSchema,
} from "DDD/action-objects/StageProductUpdate";
import { MapOutput } from "interfaces/interfaces";
import { cleanMaterialNoPlants, getMaterialNoPlantInputError } from "modules/proposals/utils/materialNoPlants";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import DirtyFieldsTracker from "modules/proposals/DirtyFieldsTracker";
import { ProposalFormsContext } from "modules/proposals/providers/ProposalFormsContextProvider";
import StageProductContext from "modules/proposals/forms/StageProductContext";
import MapModal from "modules/Mapmodal/MapModal";
import { evolve, map } from "ramda";
import { toCents } from "utils/money/toCents";
import { toDollars } from "utils/money/toDollars";
import AddAdditionalCostDrawer from "./AddAdditionalCostDrawer";
import AddEquipmentDrawer from "./AddEquipmentDrawer";
import AddLaborDrawer from "./AddLaborDrawer";
import AddMaterialDrawer from "./AddMaterialDrawer";
import { useDraftContext } from "./DraftContext";
import DefaultSection from "./DefaultSection";
import FilesAndNotes from "./FilesAndNotes";
import MaterialConfigModal from "./MaterialConfigModal";
import SubcontractedSection from "./SubcontractedSection";
import TruckingConfigModal from "./TruckingConfigModal";
import UnitPricedSection from "./UnitPricedSection";

type SchemaInput = UpdateProposalProductInputSchema;
type SchemaMaterial = SchemaInput["material"];
type SchemaPlantProduct = NonNullable<SchemaInput["plantProducts"]>[number];
type SchemaEquipmentPiece = SchemaInput["equipmentPieces"][number];
type SchemaCrew = SchemaInput["crews"][number];
type SchemaAdditionalCost = SchemaInput["additionalCosts"][number];
type SchemaTransferTruck = SchemaInput["transferTrucks"][number];

const numOrNull = (v: number | string | undefined | null): number | null =>
  typeof v === "number" && !isNaN(v) ? v : null;

const falsyToNull = <T,>(v: T): T | null => v || null;

const toInt = (v: number | string): number => {
  if (v === "" || String(v).trim() === "") return 0;
  return typeof v === "number" ? v : parseInt(String(v), 10) || 0;
};

const cleanAttribute = (v: unknown): number | null => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const dollarsToCentsOrNull = (v: number | string | null | undefined): number | null => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? toCents(n) : null;
};

const cleanAttributes = (attrs: SchemaMaterial["attributes"]) =>
  attrs ? Object.fromEntries(Object.entries(attrs).map(([k, v]) => [k, cleanAttribute(v)])) : attrs;

const cleanOverrideNumber = (v: number | string | undefined | null): number | null => {
  if (v === "" || v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const cleanTruckQuantity = (v: number | string | undefined | null): number => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const cleanMaterial = (material: SchemaMaterial) => ({
  ...material,
  dailyUnitsComplete: numOrNull(material.dailyUnitsComplete as number)?.toString() ?? null,
  materialNeeded: material.materialNeeded ? numOrNull(Number(material.materialNeeded)) : null,
  attributes: cleanAttributes(material.attributes),
  unitPrice: dollarsToCentsOrNull(material.unitPrice),
});

const minutesToHrs = (v: number | undefined | null): number | null => (v ? +(v / 60) : null);

const cleanPlantProducts = (pp: SchemaPlantProduct[]) =>
  pp?.length
    ? pp.map((p) => ({
        ...p,
        materialNeededOverride: numOrNull(p.materialNeededOverride),
        rateOverride: numOrNull(p.rateOverride) != null ? Math.round(p.rateOverride! * 100) : null,
        applicationRateOverride: numOrNull(p.applicationRateOverride),
        areaCoverage: falsyToNull(p.areaCoverage),
        rateOnWeight: p.rateOnWeight ?? null,
        transferTruckRoundTripTimeHrsOverride: falsyToNull(p.transferTruckRoundTripTimeHrsOverride),
        transferTruckLoadTimeBuffer: minutesToHrs(p.transferTruckLoadTimeBuffer),
        transferTruckUnloadTimeBuffer: minutesToHrs(p.transferTruckUnloadTimeBuffer),
      }))
    : undefined;

const cleanEquipmentPieces = (items: SchemaEquipmentPiece[]) =>
  items.map((item) => ({
    ...item,
    quantity: cleanOverrideNumber(item.quantity),
    rate: cleanOverrideNumber(item.rate),
    timeOnJob: cleanOverrideNumber(item.timeOnJob),
    timeTravel: cleanOverrideNumber(item.timeTravel),
  }));

const cleanCrews = (items: SchemaCrew[]) =>
  items.map((item) => ({
    ...item,
    quantity: cleanOverrideNumber(item.quantity),
    rate: cleanOverrideNumber(item.rate),
    timeOnjob: cleanOverrideNumber(item.timeOnjob),
    timeTravel: cleanOverrideNumber(item.timeTravel),
  }));

const cleanAdditionalCosts = (items: SchemaAdditionalCost[]) =>
  items.map((item) => ({
    ...item,
    cost: cleanOverrideNumber(item.cost),
    feeAmount: cleanOverrideNumber(item.feeAmount),
    quantity: cleanOverrideNumber(item.quantity),
  }));

const cleanTransferTrucks = (items: SchemaTransferTruck[]) =>
  items.map((item) => ({
    ...item,
    quantity: cleanTruckQuantity(item.quantity),
    rate: cleanOverrideNumber(item.rate),
  }));

type ExpandedProductProps = {
  product: ProposalProduct;
  stage: ProposalStage;
  expandedProductId: string | false;
};

export default React.memo(function ExpandedProduct({
  product,
  stage,
  expandedProductId,
}: ExpandedProductProps) {
  const { show } = useGlobalMessage();
  const { isReadOnly } = useDraftContext();
  const {
    setProductSubmitFunction,
    setProductDirtyStatus,
    setProductFormStatus,
    organizationSettings,
  } = useContext(ProposalFormsContext);
  const [updateProposalProduct, { isSuccess }] = useUpdateProposalProduct();
  const [addProposalEquipmentPieces] = useAddProposalEquipmentPieces();
  const [addProposalCrews] = useAddProposalCrews();
  const [addProposalAdditionalCosts] = useAddProposalAdditionalCosts();
  const [deleteProposalMaterialNoPlant] = useDeleteProposalMaterialNoPlant();
  const [deleteProposalPlantProduct] = useDeleteProposalPlantProduct();

  const materialConfig = useModalState();
  const truckingConfig = useModalState();
  const mapModal = useModalState();
  const equipmentDrawer = useModalState();
  const laborDrawer = useModalState();
  const additionalCostDrawer = useModalState();
  const materialDrawer = useModalState();

  const averageWorkingHoursPerDay = +(organizationSettings?.["average_working_hours_per_day"] ?? 0);
  const transferTrucks = product.transferTrucks ?? [];

  const isSubcontracted = product.material.subcontracted;
  const isUnitPriced = product.material.unitPriceProduct;

  const [mapDataProduct, setMapDataProduct] = useState(product.mapData ?? "");

  const defaultValues: UpdateProposalProductInputSchema = useMemo(
    () => {
      const values = getDefaultValues({ product, transferTrucks, averageWorkingHoursPerDay });
      return {
        ...values,
        material: {
          ...values.material,
          unitPrice:
            values.material.unitPrice === "" || values.material.unitPrice == null
              ? null
              : Number(toDollars(values.material.unitPrice)),
        },
        subcontractorCost:
          values.subcontractorCost === "" || values.subcontractorCost == null
            ? 0
            : Number(toDollars(values.subcontractorCost)),
      };
    },
    [
      product.id,
      product.jobSize,
      product.profitPercentageOverride,
      product.material.unitPriceOverride,
      product.overallTotalOverride,
      averageWorkingHoursPerDay,
      product.additionalCosts,
      product.costTotal,
      product.overallTotal,
      product.profit,
    ]
  );

  const methods = useForm<UpdateProposalProductInputSchema>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues,
  });

  const prevDefaultsRef = useRef(defaultValues);

  const convertInputHoursToSeconds = useCallback((field: number) => {
    if (field === 0) return 0;
    return field ? Math.floor(field * 3600) : null;
  }, []);

  const submitForm = useCallback(async (): Promise<boolean> => {
    let saveResult: boolean | undefined;
    // @ts-ignore
    await methods.handleSubmit(async (input) => {
      saveResult = await onSubmit(input);
    })();
    return saveResult ?? false;
  }, [methods]);

  const onSubmit = useCallback(
    async (input: SchemaInput) => {
      (document.activeElement as HTMLElement)?.blur();
      setProductFormStatus(product.id, "pending");
      setProductDirtyStatus(product.id, false);

      const materialNoPlantError = getMaterialNoPlantInputError(input.materialNoPlants);
      if (materialNoPlantError) {
        setProductFormStatus(product.id, "rejected");
        setProductDirtyStatus(product.id, true);
        show({
          message: materialNoPlantError,
          options: { variant: "error" },
        });
        return false;
      }

      const parsedInput = evolve({
        jobSize: toInt,
        material: cleanMaterial,
        materialNoPlants: cleanMaterialNoPlants,
        transferTrucks: cleanTransferTrucks,
        equipmentPieces: map(
          evolve({ timeOnJob: convertInputHoursToSeconds, timeTravel: convertInputHoursToSeconds })
        ),
        crews: map(
          evolve({ timeOnjob: convertInputHoursToSeconds, timeTravel: convertInputHoursToSeconds })
        ),
        additionalCosts: cleanAdditionalCosts,
        profitPercentageOverride: numOrNull,
        jobDurationDaysOverride: numOrNull,
        jobDurationOverride: numOrNull,
        roundTripTravelTimeHours: numOrNull,
        overallTotalOverride: falsyToNull,
        plantProducts: cleanPlantProducts,
        subcontractorCost: dollarsToCentsOrNull,
      })({
        ...input,
        equipmentPieces: cleanEquipmentPieces(input.equipmentPieces),
        crews: cleanCrews(input.crews),
      });

      const result = await updateProposalProduct({ variables: { input: parsedInput as any } });
      const success = isSuccess(result.data);

      if (success) {
        setProductFormStatus(product.id, "fulfilled");
        setProductDirtyStatus(product.id, false);
        methods.reset(methods.getValues());
      } else {
        setProductFormStatus(product.id, "rejected");
        setProductDirtyStatus(product.id, true);
        show({
          message: getGraphQLErrorMessage(
            result,
            getLocalString("proposals.update-proposal-product-failure")
          ),
          options: { variant: "error" },
        });
      }

      return success;
    },
    [updateProposalProduct, setProductFormStatus, setProductDirtyStatus, methods, product.id, show]
  );

  useEffect(() => {
    if (!isReadOnly && methods.formState.isDirty && expandedProductId !== product.id) {
      submitForm();
    }
  }, [expandedProductId, product.id]);

  useEffect(() => {
    if (isReadOnly) return;
    // @ts-ignore
    setProductSubmitFunction(product.id, methods.handleSubmit(onSubmit));
  }, [product, methods.handleSubmit, onSubmit, isReadOnly]);

  useEffect(() => {
    if (prevDefaultsRef.current === defaultValues) return;
    prevDefaultsRef.current = defaultValues;
    if (!methods.formState.isDirty) {
      methods.reset(defaultValues);
    }
  }, [defaultValues, methods]);

  const onEnterPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keyCode = e.which || e.keyCode;
    if (
      (e.key.toLowerCase() === "enter" || keyCode?.toString() === "13") &&
      (e.target as HTMLElement).getAttribute("role") !== "textbox"
    ) {
      setProductDirtyStatus(product.id, false);
      submitForm();
    }
  };

  const saveMapValue = useCallback(
    (data: MapOutput) => {
      if (product?.material?.measurementJob?.trim() === ProductMeasurementJob.UNIT) {
        return data?.measurements?.area?.feet ?? data?.measurements?.area?.yards ?? 0;
      }
      const translation = (measurementMapTranslations as any)[
        product.material.measurementJob?.trim()
      ];
      if (!translation) return 0;
      const [measurement, unit] = translation.split(".");
      return (data.measurements as any)[measurement]?.[unit] ?? 0;
    },
    [product.material.measurementJob]
  );

  const submitAndAddEquipment = useCallback(
    async (ids: string[]) => {
      await submitForm();
      await addProposalEquipmentPieces({
        variables: {
          proposalProductId: product.id,
          equipmentPieces: ids.map((id) => ({ id, quantity: 1 })),
        },
      });
    },
    [submitForm, addProposalEquipmentPieces, product.id]
  );

  const submitAndAddLabor = useCallback(
    async (ids: string[]) => {
      await submitForm();
      await addProposalCrews({
        variables: { crewIds: ids, proposalProductId: product.id },
      });
    },
    [submitForm, addProposalCrews, product.id]
  );

  const submitAndAddAdditionalCost = useCallback(
    async (ids: string[]) => {
      await submitForm();
      await addProposalAdditionalCosts({
        variables: { additionalCosts: ids, proposalProductId: product.id },
      });
    },
    [submitForm, addProposalAdditionalCosts, product.id]
  );

  const handleDeleteEquipment = useCallback(
    (id: string) => {
      const current = methods.getValues("equipmentPieces") ?? [];
      methods.setValue(
        "equipmentPieces",
        current.filter((item: any) => item.id !== id),
        { shouldDirty: true }
      );
    },
    [methods]
  );

  const handleDeleteLabor = useCallback(
    (id: string) => {
      const current = methods.getValues("crews") ?? [];
      methods.setValue(
        "crews",
        current.filter((item: any) => item.id !== id),
        { shouldDirty: true }
      );
    },
    [methods]
  );

  const handleDeleteAdditionalCost = useCallback(
    (id: string) => {
      const current = methods.getValues("additionalCosts") ?? [];
      methods.setValue(
        "additionalCosts",
        current.filter((item: any) => item.id !== id),
        { shouldDirty: true }
      );
    },
    [methods]
  );

  const handleDeleteMaterial = useCallback(
    (id: string) => {
      const current = methods.getValues("materialNoPlants") ?? [];
      methods.setValue(
        "materialNoPlants",
        current.filter((item: any) => item.id !== id),
        { shouldDirty: true }
      );
      deleteProposalMaterialNoPlant({ variables: { id } });
    },
    [deleteProposalMaterialNoPlant, methods]
  );

  const handleDeletePlantProduct = useCallback(
    (plantProductId: string) => {
      deleteProposalPlantProduct({ variables: { id: plantProductId } });
    },
    [deleteProposalPlantProduct]
  );

  const editHandlers = isReadOnly
    ? {}
    : {
        onOpenMaterialConfig: materialConfig.onOpen,
        onOpenTruckingConfig: truckingConfig.onOpen,
        onAddEquipment: equipmentDrawer.onOpen,
        onAddLabor: laborDrawer.onOpen,
        onAddAdditionalCost: additionalCostDrawer.onOpen,
        onAddMaterial: materialDrawer.onOpen,
        onMeasureJob: mapModal.onOpen,
        onDeleteEquipment: handleDeleteEquipment,
        onDeleteLabor: handleDeleteLabor,
        onDeleteAdditionalCost: handleDeleteAdditionalCost,
        onDeleteMaterial: handleDeleteMaterial,
        onDeletePlantProduct: handleDeletePlantProduct,
      };

  const renderSection = () => {
    if (isSubcontracted) {
      return <SubcontractedSection product={product} onMeasureJob={mapModal.onOpen} />;
    }
    if (isUnitPriced) {
      return (
        <UnitPricedSection
          product={product}
          onMeasureJob={mapModal.onOpen}
          onAddAdditionalCost={isReadOnly ? undefined : additionalCostDrawer.onOpen}
          onDeleteAdditionalCost={handleDeleteAdditionalCost}
        />
      );
    }
    return <DefaultSection product={product} stage={stage} {...editHandlers} />;
  };

  return (
    <StageProductContext.Provider
      // @ts-ignore
      value={{ product, stage, defaultValues, onSubmit, transferTrucksById: {} }}
    >
      <FormProvider {...methods}>
        <DirtyFieldsTracker id={product.id} />
        <MDBox
          component="form"
          role="form"
          onKeyDown={isReadOnly ? undefined : onEnterPress}
          sx={{
            "& fieldset[disabled] .MuiInputBase-root.MuiInputBase-root": {
              backgroundColor: "#f0f2f5",
              pointerEvents: "none",
            },
          }}
        >
          <fieldset disabled={isReadOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            {renderSection()}
            <MDBox sx={{ backgroundColor: "#f9fafc", p: "12px" }}>
              <FilesAndNotes product={product} />
            </MDBox>
          </fieldset>
        </MDBox>

        <MaterialConfigModal
          open={materialConfig.open}
          onClose={materialConfig.onClose}
          product={product}
          onSave={submitForm}
        />
        <TruckingConfigModal
          open={truckingConfig.open}
          onClose={truckingConfig.onClose}
          product={product}
        />

        <Controller
          name="mapData"
          control={methods.control}
          render={({ field }) => (
            <MapModal
              initialArea={mapDataProduct ? JSON.parse(mapDataProduct) : null}
              initialCenter={
                stage?.address?.latitude && stage?.address?.longitude
                  ? [Number(stage.address.latitude), Number(stage.address.longitude)]
                  : null
              }
              open={mapModal.open}
              guided={false}
              hideLocation
              onClose={mapModal.onClose}
              onOpen={mapModal.onOpen}
              showTools={[
                product.material.measurementJob?.trim() as keyof typeof ProductMeasurementJob,
              ]}
              onSave={async (data: MapOutput) => {
                setProductDirtyStatus(product.id, false);
                setMapDataProduct(JSON.stringify(data.geojson));
                field.onChange(JSON.stringify(data.geojson));
                methods.setValue("jobSize", saveMapValue(data));
                methods.setValue("mapData", JSON.stringify(data.geojson));
                return submitForm();
              }}
            />
          )}
        />
      </FormProvider>

      <AddEquipmentDrawer
        open={equipmentDrawer.open}
        onClose={equipmentDrawer.onClose}
        onAdd={submitAndAddEquipment}
      />
      <AddLaborDrawer
        open={laborDrawer.open}
        onClose={laborDrawer.onClose}
        onAdd={submitAndAddLabor}
      />
      <AddAdditionalCostDrawer
        open={additionalCostDrawer.open}
        onClose={additionalCostDrawer.onClose}
        onAdd={submitAndAddAdditionalCost}
      />
      <AddMaterialDrawer
        open={materialDrawer.open}
        onClose={materialDrawer.onClose}
        product={product}
      />
    </StageProductContext.Provider>
  );
});
